import { Box, Grid, Popover, styled, Typography, useMediaQuery } from '@mui/material'
import MuiTooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import commonStyles from 'commonStyles/index.module.css'
import gridStyles from 'commonStyles/removeCartesianGrid.module.css'
import * as Flags from 'country-flag-icons/react/3x2'
import React, { forwardRef, useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatNumberDecimal } from 'utils/formatNumber'
import { COLOR, DOWNLOAD_FILE_NAME, METRIC_ENDPOINT_RANK, METRIC_ENDPOINT_WEIGHT, PILLAR_INFO } from '../../../constant'
import { useAppSelector } from '../../../hooks'
import { country } from '../../../store/AIMetricOT/reducerSlice'
import { BarChartGlobalData, BarChartNationalData, SortOrder } from '../../../types'
import {
  calculateStringWidth,
  calculateWeightedGlobalBarChart,
  generateGlobalBarChartData,
  generateNationalBarChartData,
  generateNationalRankedChartData,
  getCountryCodeFromCountryLongCode,
  getCountryNameFromCountryLongCode,
  getFilteredStrengthsData,
  getFilteredWeaknessesData,
  isDataKeyExistsInData,
} from '../../../utils'
import CountryReport from '../../Chart/CountryReport'
import { useDownloadCountryReport } from '../Download/useDownloadCountryReport'
import NationalPopupBarChart from '../NationalPopupBarChart/NationalPopupBarChart'
import styles from './styles.module.css'
import { Spin } from 'antd'
import { LoadingOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons'
import StrengthWeaknessSection from './StrengthWeaknessSection'

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FFFFFD',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: '14px',
    border: '1px solid #dadde9',
    textAlign: 'justify',
    fontFamily: 'CircularStdBook, sans-serif',
  },
}))

export interface NationalBarChartProps {
  isOpenSW: boolean
  setIsOpenSW: React.Dispatch<boolean>
  is4Downloaded?: boolean
}

const CustomTooltip: React.FC<any> = (props) => {
  const { active, payload, metricGroups, countryData, countryLongCode, yearFilter } = props

  if (active && payload && payload.length) {
    const dataTooltip = payload[0].payload

    const dataRank = generateNationalRankedChartData(metricGroups, countryData, {
      globalYear: yearFilter.currentYear,
      metricName: payload[0].payload?.metricNameRaw,
    })
    const countryName = getCountryNameFromCountryLongCode(countryData, countryLongCode)!
    const gRank = dataRank?.find((el) => el.countryName === countryName)?.cRank
    return (
      <div className='custom-tooltip'>
        <div className={styles.span}>
          Click on an individual indicator to view its Global Ranking.
          {/* {dataTooltip?.metricName} */}
          <br />
          <span style={{ fontFamily: 'CircularStdBold, sans-serif' }}>
            Normalized Score: {formatNumberDecimal(dataTooltip?.score, 2)}
          </span>
          <br />
          Global Ranking: {gRank}/{dataRank.length}
        </div>
      </div>
    )
  }

  return null
}

const NationalBarChart = forwardRef<HTMLDivElement, NationalBarChartProps>(
  ({ isOpenSW, setIsOpenSW, is4Downloaded }: NationalBarChartProps, popUpRef) => {
    const [focusCell, setFocusCell] = useState<number | null>(null)
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
    const [metricNameChosen, setMetricNameChosen] = useState<string | null>(null)
    const [metricNameColor, setMetricNameColor] = useState<string>('rgb(36, 148, 153)')
    const countryLongCode = useAppSelector(country)
    const globarBarChartData = useAppSelector(({ vibrancy }) => vibrancy.barChartGlobalData)
    const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
    const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
    const sortBy = useAppSelector(({ vibrancy }) => vibrancy.sortBy)
    const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)
    const { yearFilter, metricValue } = useAppSelector(({ vibrancy }) => vibrancy.configuration)
    const openPopover = Boolean(anchorEl)
    const idPopover = openPopover ? 'simple-popover' : undefined
    const Flag = (Flags as any)[getCountryCodeFromCountryLongCode(countryData, countryLongCode) || 'US']
    const renderChartData = useMemo(
      () => calculateWeightedGlobalBarChart(metricGroups, metricValue, countryData, yearFilter?.currentYear),
      [metricGroups, metricValue, countryData, yearFilter?.currentYear],
    )
    const [isDownloadingCountryReport, setIsDownloadingCountryReport] = useState<boolean>(false)
    const countryCode = getCountryCodeFromCountryLongCode(countryData, countryLongCode)
    const strengths = useMemo(
      () => getFilteredStrengthsData(countryCode ?? 'US', yearFilter.currentYear, metricValue),
      [yearFilter.currentYear, countryCode, metricValue],
    )

    const Weaknesses = useMemo(
      () => getFilteredWeaknessesData(countryCode ?? 'US', yearFilter.currentYear, metricValue),
      [yearFilter.currentYear, countryCode, metricValue],
    )

    const data = useMemo<BarChartNationalData>(() => {
      if (metricGroups.length === 0 || countryData.length === 0 || yearFilter.yearRange.length === 0) return []

      const dataReturn = generateNationalBarChartData(metricGroups, countryData, {
        countryLongCode: countryLongCode,
        globalYear: yearFilter.currentYear,
        metricValue: metricValue,
      })

      if (sortBy === SortOrder.overallPosition) {
        return dataReturn?.sort((a, b) => b.score - a.score)
      }
      return dataReturn
    }, [countryData.length, metricGroups.length, countryLongCode, renderChartTimes, sortBy])

    const barChartGlobalData = useMemo<BarChartGlobalData>(() => {
      if (metricGroups.length === 0 || countryData.length === 0 || yearFilter.yearRange.length === 0) return []

      const dataReturn = generateGlobalBarChartData(metricGroups, renderChartData, {
        globalYear: yearFilter.currentYear,
        metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
        metricEndpointRank: METRIC_ENDPOINT_RANK,
        metricValue: metricValue,
      })
      return dataReturn
    }, [countryData.length, metricGroups.length, renderChartTimes])

    const dataWithSW = useMemo(
      () =>
        data.map((eachIndex) => {
          const strengthIndex = strengths?.findIndex((i) => `${i.metricSmallName}_display` === eachIndex.variableName)
          const weaknessIndex = Weaknesses?.findIndex((i) => `${i.metricSmallName}_display` === eachIndex.variableName)
          if (strengths && (strengthIndex || strengthIndex === 0) && strengthIndex >= 0) {
            return {
              ...eachIndex,
              isStrength: true,
              isWeakness: false,
              sw_index: strengths[strengthIndex].index,
            }
          } else if (Weaknesses && (weaknessIndex || weaknessIndex === 0) && weaknessIndex >= 0) {
            return {
              ...eachIndex,
              isStrength: false,
              isWeakness: true,
              sw_index: Weaknesses[weaknessIndex].index,
            }
          } else {
            return {
              ...eachIndex,
              isStrength: false,
              isWeakness: false,
              sw_index: null,
            }
          }
        }),
      [data],
    )

    const yAxisWidth = useMemo(() => {
      if (!data || data.length === 0) return 0
      const countryNameWidths = data.map((item) =>
        calculateStringWidth(item.metricName, 'normal 14px "CircularStdMedium"'),
      )
      const maxWidth = Math.max(...countryNameWidths)

      return maxWidth + 55
    }, [data])

    // Download Country Reports:
    const { handleDownload, divRef } = useDownloadCountryReport()
    const handleCountryReportDownload = async () => {
      setIsDownloadingCountryReport(true)

      await handleDownload('pdf', {
        fileName: `${DOWNLOAD_FILE_NAME?.countryReport}-${countryLongCode}`,
        isSquareDownload: true,
        orientation: 'portrait',
      })
      setIsDownloadingCountryReport(false)
    }

    const legendList = useMemo(
      () =>
        PILLAR_INFO.filter((el) => isDataKeyExistsInData(globarBarChartData, el)).map((pillar) => ({
          id: pillar.dataKey,
          type: 'square' as 'square',
          value: pillar.displayName,
          color: pillar.color,
        })),
      [globarBarChartData],
    )

    const getSWColor = (entry: any) => {
      if (isOpenSW && entry.isStrength) {
        return '#10b51e'
      } else if (isOpenSW && entry.isWeakness) {
        return '#e82525'
      } else {
        return undefined
      }
    }

    const isSmallerThan1536 = useMediaQuery('(max-width:1536px)')
    const isSmallerThan600 = useMediaQuery('(max-width:600px)')

    return (
      <Box position={'relative'} overflow={'hidden'}>
        <Spin
          spinning={isDownloadingCountryReport}
          tip={<span style={{ color: '#172e5c' }}>Downloading...</span>}
          indicator={<LoadingOutlined style={{ color: '#172e5c' }} spin />}
          size='large'
        >
          <Box
            sx={{
              marginTop: '30px',
              marginBottom: '30px',
              marginLeft: 'auto',
              marginRight: 'auto',
              backgroundColor: '#c2ddee',
              padding: '10px 25px 10px 25px',
              borderRadius: '30px',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Grid
              container
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
              spacing={2}
            >
              <Grid item>
                {/* <Link to='/reports'> */}
                <Flag height={22} />
                {/* </Link> */}
              </Grid>
              <Grid item>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    mt: { xs: '0px', sm: '12px' },
                    mb: '12px',
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <Typography
                    component='span'
                    sx={{ lineHeight: 1, fontWeight: 400, fontSize: { xs: '18px', sm: '22px' } }}
                  >
                    Overall ranking:{' '}
                  </Typography>
                  <Typography
                    component='span'
                    sx={{
                      lineHeight: 1,
                      fontWeight: 400,
                      borderBottom: `5px solid ${COLOR.NAVY2}`,
                      fontSize: { xs: '18px', sm: '22px' },
                      width: 'fit-content',
                    }}
                  >
                    {barChartGlobalData.findIndex(
                      (countryEl) =>
                        countryEl.countryName === getCountryNameFromCountryLongCode(countryData, countryLongCode),
                    ) + 1}{' '}
                    of {barChartGlobalData?.length || 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isSmallerThan600 ? 'column' : 'row',
                justifyContent: 'center',                          // Step(4/4) - Closed Strength and weaknesses temporarily upon client's request. This value will be space-between
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div
                style={{
                  fontWeight: 400,
                  textDecoration: 'none',
                  background: `${COLOR.NAVY2}`,
                  padding: '4px 15px',
                  borderRadius: '20px',
                  color: 'white',
                  lineHeight: 1.2,
                  fontSize: '0.8rem',
                  display: is4Downloaded ? 'none' : undefined,
                  cursor: 'pointer',
                }}
                onClick={handleCountryReportDownload}
              >
                DOWNLOAD COUNTRY REPORT
              </div>
              {/* <div                                    // Step(2/4) - Closed Strength and weaknesses temporarily upon client's request. Uncomment to enable it
                style={{
                  fontWeight: 700,
                  textDecoration: 'none',
                  background: '#fff',
                  padding: '4px 15px',
                  borderRadius: '20px',
                  color: `${COLOR.DARK}`,
                  lineHeight: 1.2,
                  fontSize: '0.8rem',
                  display: is4Downloaded ? 'none' : undefined,
                  cursor: 'pointer',
                }}
                onClick={() => setIsOpenSW(!isOpenSW)}
              >
                Strengths + Weaknesses
                <span style={{ marginLeft: '10px' }}>{isOpenSW ? <MinusOutlined /> : <PlusOutlined />}</span>
              </div> */}
            </Box>
            {/* {isOpenSW && strengths && Weaknesses && (                                  // Step(3/4) -Closed Strength and weaknesses temporarily upon client's request. Uncomment to enable it
              <StrengthWeaknessSection strengths={strengths} weaknesses={Weaknesses} />
            )} */}
          </Box>
          <Popover
            id={idPopover}
            open={openPopover}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'center',
            }}
            sx={{
              '& .MuiPopover-paper': {
                borderRadius: '30px !important',
              },
            }}
          >
            <Box
              sx={{
                height: { xs: '470px', sm: '450px' },
                padding: '25px 20px 10px 20px',
                borderRadius: '20px',
              }}
            >
              <NationalPopupBarChart
                metricName={metricNameChosen || ''}
                countryName={getCountryNameFromCountryLongCode(countryData, countryLongCode)!}
                onClose={() => setAnchorEl(null)}
                color={metricNameColor}
              />
            </Box>
          </Popover>
          <Box
            display={isSmallerThan1536 ? 'grid' : 'flex'}
            flexDirection={'row'}
            sx={{
              padding: '8px 0 16px 0',
              justifyContent: 'space-between',
              gridTemplateColumns: isSmallerThan1536 ? `repeat(auto-fill, minmax(168px, 1fr))` : undefined,
            }}
          >
            {legendList?.map((l) => (
              <Box
                key={l.id}
                flexGrow={1}
                display={'flex'}
                flexDirection={'row'}
                flexWrap={'nowrap'}
                alignItems={'center'}
                gap={1}
              >
                <Box sx={{ width: 14, height: 14, backgroundColor: l.color }}></Box>
                <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>{l.value}</span>
              </Box>
            ))}
          </Box>

          <Box
            sx={{ overflowY: 'auto', overflowX: is4Downloaded ? 'hidden' : 'auto' }}
            className={is4Downloaded ? undefined : commonStyles['scrollbar-common']}
          >
            <ResponsiveContainer
              minWidth={`${is4Downloaded ? '780px' : '600px'}`}
              width='100%'
              height={(data ?? []).length * 35}
              className={gridStyles.grid}
            >
              <BarChart
                layout='vertical'
                data={dataWithSW}
                margin={{
                  top: 5,
                  left: -40,
                  bottom: 5,
                }}
                barSize={20}
              >
                <XAxis
                  type='number'
                  tickLine={false}
                  axisLine={false}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  allowDataOverflow
                  domain={[0, 'dataMax + 5']}
                />
                <YAxis
                  className='text-bar-chart-axis'
                  dataKey='metricName'
                  tickLine={false}
                  axisLine={false}
                  type='category'
                  tick={(props) => {
                    const propData = dataWithSW[props.index]
                    return (
                      <>
                        <text {...props} fill={getSWColor(propData) ?? 'black'}>
                          {isOpenSW && propData.isStrength && <tspan fontWeight='bold'>{propData?.sw_index}</tspan>}
                          {isOpenSW && propData.isWeakness && <tspan fontWeight='bold'>{propData?.sw_index}</tspan>}
                          <tspan> {propData.metricName}</tspan>
                        </text>
                      </>
                    )
                  }}
                  width={yAxisWidth}
                />
                <CartesianGrid horizontal={false} />
                <Tooltip
                  content={
                    <CustomTooltip
                      metricGroups={metricGroups}
                      countryData={countryData}
                      countryLongCode={countryLongCode}
                      yearFilter={yearFilter}
                    />
                  }
                  cursor={{ fill: 'transparent' }}
                />

                <Bar dataKey='score'>
                  {dataWithSW.map((entry, index) => (
                    <Cell
                      cursor='pointer'
                      fill={
                        focusCell === index
                          ? PILLAR_INFO.find((el) => el.name === entry.pillar)?.lightColor
                          : PILLAR_INFO.find((el) => el.name === entry.pillar)?.color
                      }
                      stroke={getSWColor(entry)}
                      style={
                        isOpenSW
                          ? {
                            filter: `drop-shadow(0px 0px 3px ${getSWColor(entry)})`,
                          }
                          : {}
                      }
                      key={`cell-${index}`}
                      onClick={() => {
                        setAnchorEl((popUpRef as any)?.current)
                        setMetricNameChosen(entry.metricNameRaw)
                        setMetricNameColor(
                          PILLAR_INFO.find((el) => el.name === entry.pillar)?.color || 'rgb(36, 148, 153)',
                        )
                      }}
                      onMouseOver={() => {
                        setFocusCell(index)
                      }}
                      onMouseOut={() => {
                        setFocusCell(null)
                      }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <Box sx={{ textAlign: 'center', marginLeft: '25%', my: '12px' }}>
              <Typography sx={{ fontSize: { xs: '16px', sm: '16px' } }}>
                <HtmlTooltip
                  followCursor
                  placement='top'
                  title={`The normalized score is the value of a metric rescaled to a common range of [0, 100] using min-max
            normalization. This allows for comparisons across different indicators and units of measurement.`}
                >
                  <span style={{ cursor: 'pointer' }}>Normalized Score</span>
                </HtmlTooltip>
              </Typography>
            </Box>
          </Box>
        </Spin>
        {!is4Downloaded && (
          <Box
            ref={divRef}
            className='country-report-component'
            sx={{ minHeight: '2800px !important', visibility: isDownloadingCountryReport ? 'visible' : 'hidden' }}
          >
            {/*minHeight: '2740px !important'*/}
            <CountryReport />
          </Box>
        )}
      </Box>
    )
  },
)

NationalBarChart.displayName = 'NationalVibrancyRankingBarChart'

export default NationalBarChart
